import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  CreatePaymentPayload, 
  PaymentResponse, 
  PaymentHistoryItem,
  WalletAdvance,
  PaymentDetails,
  PaymentDetailItem,
  PaymentApiPayload,
} from './collectPayment.types';
import { toISODateString } from '../../../utils/dateUtils';
import { getRequest } from '../../../services/httpService';
import { getPatientWalletBalanceURL, PatientWalletBalance } from '../Advance/advance.helper';

// API Base URL - adjust based on your environment
const API_BASE_URL = '/api/v1/billing';

/**
 * Fetch payment details for an invoice
 */
export const fetchPaymentDetails = async (invoiceId: string | number): Promise<PaymentDetails> => {
  const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payment-details`);
  if (!response.ok) {
    throw new Error('Failed to fetch payment details');
  }
  return response.json();
};

/**
 * Fetch payment history for an invoice
 */
export const fetchPaymentHistory = async (invoiceId: string | number): Promise<PaymentHistoryItem[]> => {
  const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payments`);
  if (!response.ok) {
    throw new Error('Failed to fetch payment history');
  }
  return response.json();
};

/**
 * Fetch wallet advances for a patient
 */
export const fetchWalletAdvances = async (patientId: string | number): Promise<{
  balance: number;
  advances: WalletAdvance[];
}> => {
  const response = await fetch(`${API_BASE_URL}/patients/${patientId}/wallet`);
  if (!response.ok) {
    throw new Error('Failed to fetch wallet advances');
  }
  return response.json();
};

/**
 * Create a new payment
 */
export const createPayment = async (payload: CreatePaymentPayload): Promise<PaymentResponse> => {
  const response = await fetch(`${API_BASE_URL}/payments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error('Failed to create payment');
  }
  return response.json();
};

/**
 * Update an existing payment
 */
export const updatePayment = async (
  paymentId: string | number, 
  payload: Partial<CreatePaymentPayload>
): Promise<PaymentResponse> => {
  const response = await fetch(`${API_BASE_URL}/payments/${paymentId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error('Failed to update payment');
  }
  return response.json();
};

/**
 * Delete a payment
 */
export const deletePayment = async (paymentId: string | number): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/payments/${paymentId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to delete payment');
  }
};

/**
 * Save invoice as credit
 */
export const saveAsCredit = async (invoiceId: string | number): Promise<PaymentResponse> => {
  const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/save-as-credit`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error('Failed to save as credit');
  }
  return response.json();
};

// ============================================================================
// TanStack Query Hooks
// ============================================================================

/**
 * Hook to fetch payment details
 */
export const usePaymentDetails = (invoiceId?: string | number) => {
  return useQuery({
    queryKey: ['paymentDetails', invoiceId],
    queryFn: () => fetchPaymentDetails(invoiceId!),
    enabled: !!invoiceId,
  });
};

/**
 * Hook to fetch payment history
 */
export const usePaymentHistory = (invoiceId?: string | number) => {
  return useQuery({
    queryKey: ['paymentHistory', invoiceId],
    queryFn: () => fetchPaymentHistory(invoiceId!),
    enabled: !!invoiceId,
  });
};

/**
 * Hook to fetch wallet advances
 */
export const useWalletAdvances = (patientId?: string | number) => {
  return useQuery({
    queryKey: ['walletAdvances', patientId],
    queryFn: () => fetchWalletAdvances(patientId!),
    enabled: !!patientId,
  });
};

/**
 * Hook to fetch patient wallet balance (from patient-advances/{patientId}/checkin/detail/)
 * Returns balance_amount which represents available wallet balance
 * 
 * @param patientId - Patient ID to fetch wallet balance for
 * @param options - Additional options
 * @param options.enabled - Whether to enable the query (default: true when patientId exists)
 */
export const usePatientWalletBalance = (
  patientId?: string | number,
  options?: { enabled?: boolean }
) => {
  return useQuery<PatientWalletBalance>({
    queryKey: ['patientWalletBalance', patientId],
    queryFn: () => getRequest<PatientWalletBalance>(getPatientWalletBalanceURL(patientId!)),
    enabled: !!patientId && (options?.enabled ?? true),
    retry: false, // Don't retry on failure to avoid multiple failed calls
    staleTime: 0, // Always fetch fresh data when modal opens
  });
};

/**
 * Hook to create payment
 */
export const useCreatePayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createPayment,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['paymentHistory', variables.invoice_id] });
      queryClient.invalidateQueries({ queryKey: ['paymentDetails', variables.invoice_id] });
      // Invalidate active-packages so Advance Balance Amount refreshes after payment
      queryClient.invalidateQueries({ queryKey: ['invoices', 'active-packages'] });
      queryClient.invalidateQueries({ queryKey: ['active-packages'] });
    },
  });
};

/**
 * Hook to update payment
 */
export const useUpdatePayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ paymentId, payload }: { paymentId: string | number; payload: Partial<CreatePaymentPayload> }) =>
      updatePayment(paymentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentHistory'] });
    },
  });
};

/**
 * Hook to delete payment
 */
export const useDeletePayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deletePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentHistory'] });
    },
  });
};

/**
 * Hook to save as credit
 */
export const useSaveAsCredit = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: saveAsCredit,
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['paymentDetails', invoiceId] });
    },
  });
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format currency amount
 */
export const formatCurrency = (amount: number, currencyCode: string = 'INR'): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });
  return formatter.format(amount);
};

/**
 * Calculate remaining amount after payments
 * Rounds to 2 decimal places to avoid floating-point precision issues
 */
export const calculateRemainingAmount = (
  netAmount: number,
  paymentHistory: PaymentHistoryItem[]
): number => {
  const totalPaid = paymentHistory.reduce((sum, payment) => sum + payment.amount, 0);
  const remaining = Math.max(0, netAmount - totalPaid);
  return Math.round(remaining * 100) / 100;
};

/**
 * Calculate total from wallet advances
 */
export const calculateWalletTotal = (advances: WalletAdvance[]): number => {
  return advances
    .filter(adv => adv.isSelected)
    .reduce((sum, adv) => sum + adv.consumeAmount, 0);
};

/**
 * Validate payment amount
 */
export const validatePaymentAmount = (
  amount: number,
  remainingAmount: number
): { valid: boolean; message?: string } => {
  if (amount <= 0) {
    return { valid: false, message: 'Amount must be greater than 0' };
  }
  if (amount > remainingAmount) {
    return { valid: false, message: 'Amount cannot exceed remaining balance' };
  }
  return { valid: true };
};


/**
 * Transform PaymentHistoryItem 
 */
const transformToPaymentDetail = (
  item: PaymentHistoryItem,
  currency: string
): PaymentDetailItem => {
  return {
    payment_mode_id: item.payment_mode_id ?? null,
    paid_amount: item.amount.toFixed(2),
    package_id: item.package_id ?? null,
    currency: currency,
    number: item.transactionNumber || item.cardTransactionNumber || null,
    date: toISODateString(item.date),
    bank_id: null,
    account_name: item.paymentMode || null, // IMPS, NEFT, RTGS, etc.
    branch_name: item.bankName || null,
    ifsc_code: null,
    remarks: item.notes || null,
    cheque_number: item.chequeNumber || null,
    cheque_date: item.chequeDate ? toISODateString(item.chequeDate) : null,
    upi_provider_id: item.upiProviderId ?? null,
    credit_transaction_consumptions: item.credit_transaction_consumptions || undefined,
  };
};

/**
 * Build payment API payload from payment history
 * 
 * @param history - Array of PaymentHistoryItem from the modal
 * @param invoiceId - Invoice ID
 * @param patientId - Patient ID
 * @param clinicId - Clinic ID
 * @param currency - Currency code (e.g., 'INR', 'USD')
 * @param paymentSource - Source of payment (e.g., 'Invoice')
 * @param remarks - Optional remarks
 * @returns PaymentApiPayload ready to send to API
 * 
 * @example
 * const payload = buildPaymentApiPayload(
 *   history,
 *   4,
 *   1398,
 *   7,
 *   'INR',
 *   'INVOICE',
 *   'UPI payment'
 * );
 */
export const buildPaymentApiPayload = (
  history: PaymentHistoryItem[],
  invoiceId: number,
  patientId: number,
  clinicId: number,
  currency: string,
  paymentAgainstType: string = 'INVOICE',
  remarks: string = ''
): PaymentApiPayload => {
  const paymentDetails: PaymentDetailItem[] = history.map((item) =>
    transformToPaymentDetail(item, currency)
  );
  
  return {
    invoice_id: invoiceId,
    patient_id: patientId,
    clinic_id: clinicId,
    payment_against_type: paymentAgainstType,
    remarks: remarks,
    payment_details: paymentDetails,
  };
};
